import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { useDIDStore } from '../stores/did-store';
import { useDID, useReleaseDID } from '../hooks';
import { formatPhoneNumber } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  Phone,
  RefreshCcw,
  PhoneForwarded,
  Printer,
  Users,
  UserCheck,
  Shield,
  X,
  TriangleAlert,
  Trash2,
} from 'lucide-react';

// Tab content components
import { FailoverTab } from './tabs/failover-tab';
import { ForwardTab } from './tabs/forward-tab';
import { FaxToEmailTab } from './tabs/fax-to-email-tab';
import { ConferenceTab } from './tabs/conference-tab';
import { CallerIDCNAMTab } from './tabs/caller-id-cnam-tab';
import { E911Tab } from './tabs/e911-tab';

import type { DIDTabId } from '../types';

// Tab order: Failover, Call Forwarding, eFax, Conference, Caller ID / CNAM, E911
const tabs: { id: DIDTabId; label: string; icon: React.ElementType }[] = [
  { id: 'failover', label: 'Failover', icon: RefreshCcw },
  { id: 'forward', label: 'Forward', icon: PhoneForwarded },
  { id: 'fax-to-email', label: 'eFax', icon: Printer },
  { id: 'conference', label: 'Conference', icon: Users },
  { id: 'caller-id-cnam', label: 'Caller ID / CNAM', icon: UserCheck },
  { id: 'e911', label: 'E911', icon: Shield },
];

export function DIDDetailModal() {
  const { selectedDID, isDetailModalOpen, activeTab, closeDetailModal, setActiveTab } =
    useDIDStore();
  const { data: did, isLoading } = useDID(selectedDID);
  const releaseDID = useReleaseDID();

  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  if (!isDetailModalOpen || !selectedDID) return null;

  const handleRelease = () => {
    releaseDID.mutate(selectedDID, {
      onSuccess: () => {
        setShowReleaseConfirm(false);
        setConfirmText('');
        closeDetailModal();
      },
    });
  };

  // Require typing "DISCONNECT" to confirm
  const canConfirmRelease = confirmText === 'DISCONNECT';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeDetailModal}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-telvia-100 rounded-lg">
                <Phone className="h-5 w-5 text-telvia-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {formatPhoneNumber(selectedDID)}
                </h2>
                <p className="text-sm text-slate-500">DID Configuration</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowReleaseConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Disconnect DID
              </Button>
              <button
                onClick={closeDetailModal}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-slate-50 px-6">
          <div className="flex gap-1 py-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all',
                    isActive
                      ? 'bg-telvia-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-200'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content - fixed height */}
        <div className="p-6 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="animate-fade-in">
              {activeTab === 'failover' && <FailoverTab did={did} />}
              {activeTab === 'forward' && <ForwardTab did={did} />}
              {activeTab === 'fax-to-email' && <FaxToEmailTab did={did} />}
              {activeTab === 'conference' && <ConferenceTab did={did} />}
              {activeTab === 'caller-id-cnam' && <CallerIDCNAMTab did={did} />}
              {activeTab === 'e911' && <E911Tab tn={selectedDID} />}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 flex justify-end">
          <Button variant="secondary" onClick={closeDetailModal}>
            Close
          </Button>
        </div>
      </div>

      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={showReleaseConfirm}
        onClose={() => {
          setShowReleaseConfirm(false);
          setConfirmText('');
        }}
        title="Disconnect DID"
        size="md"
      >
        <div className="space-y-4">
          {/* Strong Warning */}
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <TriangleAlert className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-800">
                WARNING: This action cannot be undone!
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>This phone number will be returned to the carrier</li>
                <li>All configuration (forwarding, E911, etc.) will be lost</li>
                <li>The number may be assigned to another customer</li>
                <li>You will not be able to recover this number</li>
              </ul>
            </div>
          </div>

          {/* DID Info */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">Phone number to disconnect:</p>
            <p className="text-xl font-bold text-slate-900 font-mono mt-1">
              {formatPhoneNumber(selectedDID)}
            </p>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Type <span className="font-bold text-red-600">DISCONNECT</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              placeholder="DISCONNECT"
              disabled={releaseDID.isPending}
            />
          </div>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowReleaseConfirm(false);
                setConfirmText('');
              }}
              disabled={releaseDID.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRelease}
              disabled={!canConfirmRelease}
              isLoading={releaseDID.isPending}
            >
              Disconnect DID Permanently
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}
